import { createFileRoute, Link, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useBrokerDetail, useBrokerPeers, useBrokerStocks } from '@/features/brokers/api/queries'
import {
  useBrokerTradeSplit,
  BROKER_TRADE_SPLIT_AVAILABLE,
} from '@/features/brokers/api/tradeSplit'
import {
  brokerDisplayName,
  brokerFullName,
  type BrokerPeriod,
  type BrokerStocksQuery,
} from '@/schemas/broker'
import { toPageSize } from '@/schemas/table'
import {
  brokerSearchSchema,
  toBrokerStocksQuery,
  type BrokerSearch,
  type BrokerTab,
} from '@/screens/brokers/search'
import { loadBroker } from '@/screens/brokers/BrokerScreen.loader'
import { BrokerBoardSkeleton } from '@/screens/brokers/components/broker/BrokerBoardStates'
import BrokerControls from '@/screens/brokers/components/broker/BrokerControls'
import { partialWindowNote, windowPhrase } from '@/screens/brokers/components/broker/broker-format'
import BrokerHeaderCard from '@/screens/brokers/components/broker/BrokerHeaderCard'
import BrokerHoldingsCard from '@/screens/brokers/components/broker/BrokerHoldingsCard'
import BrokerPeersCard from '@/screens/brokers/components/broker/BrokerPeersCard'
import BrokerPerformanceCard from '@/screens/brokers/components/broker/BrokerPerformanceCard'
import BrokerStockActivityCard from '@/screens/brokers/components/broker/BrokerStockActivityCard'
import BrokerStockTables from '@/screens/brokers/components/broker/BrokerStockTables'
import BrokerVsMarketCard from '@/screens/brokers/components/broker/BrokerVsMarketCard'
import WindowNote from '@/screens/brokers/components/broker/WindowNote'
import { useBrokerPicker } from '@/screens/brokers/lib/useBrokerPicker'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="By Broker" />

/**
 * One payload feeds five of this page's blocks (header through holdings), so
 * — like `/market` and `/stocks/$symbol` — the first load draws ONE page-wide
 * skeleton rather than eight card-level ones that would all be pending
 * together anyway.
 */
function BrokerPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading this broker…" header={HEADER} pageTitle="By Broker">
      <BrokerBoardSkeleton />
    </ScreenLayout>
  )
}

/**
 * Light on purpose (docs/route-loading.md, "Error components"): only
 * `RouteErrorPage` plus the static header, so it stays in the entry chunk.
 * `BROKER_NOT_FOUND` never
 * reaches here any more — `brokerDetailQueryOptions` resolves it to
 * `data === null` instead of throwing, and the screen's own body draws that
 * branch (below), the way Stock draws `company === null`. This component
 * only ever shows for a genuine failure: offline, or the request itself
 * broke.
 */
function BrokerError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Couldn't load this broker" />
}

export const Route = createFileRoute('/_app/brokers/$code')({
  validateSearch: brokerSearchSchema,
  loader: loadBroker,
  pendingComponent: BrokerPending,
  errorComponent: BrokerError,
  component: BrokerScreen,
})

/**
 * What the stocks table looks like on a fresh visit to a broker.
 *
 * Parsed out of the schema rather than written out, so "the defaults" has one
 * definition: `brokerSearchSchema` fills `page`, `pageSize`, `sort` and
 * `period`, and a change to any of them cannot leave this constant behind.
 */
const defaultSearch = (tab: BrokerTab, period: BrokerPeriod): BrokerSearch =>
  brokerSearchSchema.parse({ tab, period })

/**
 * Brokers · By Broker. The section tab strip names the page from `lg` up, so
 * `TitleRow` only keeps its `h1` for screen readers there.
 *
 * THE REVAMP MOCK (2026-10-28, `#/brokers/by-broker`): the head panel (identity, the KPI row
 * with its chips, the bought/sold split), the performance card, the two-up Top bought / Top sold
 * with Net position under them, the market comparison, the money treemap, Every stock with its
 * pager outside the card, and the peers. The picker and header print the name without its legal
 * form; the cards name the house by its short name ("How NIBL Ace traded"). No source footnote:
 * the mock closes on the peers.
 *
 * ── The tab architecture ─────────────────────────────────────────────────────
 * `tab` is a CLIENT-SIDE toggle over the single `BrokerDetail` response, not a
 * third request. One `useBrokerDetail(code, period)` call feeds the header, all
 * three performance tabs, the three ranked tables, the vs-market card and the
 * holdings card — five blocks of one payload behind one query key, so the tabs
 * cannot disagree about which session they describe.
 * The genuinely separate data on this page is `stocks` and `peers`, each its
 * own key branch with its own row shape.
 *
 * `stocks`' own page, search and sort are URL state, like every other table in
 * the app: `brokerSearchSchema` is `brokerStocksQuerySchema` plus `tab`, and
 * `toBrokerStocksQuery` projects `tab` back off before the query hook sees it
 * — `brokerKeys.stocks` keys on the whole object, so a `tab` left in would
 * refetch the table on a toggle that is not a request at all.
 */
function BrokerScreen() {
  const { code } = Route.useParams()
  const search = Route.useSearch()
  const { tab, period } = search
  const navigate = Route.useNavigate()

  const stocksQuery: BrokerStocksQuery = toBrokerStocksQuery(search)

  const detail = useBrokerDetail(code, period)
  const stocksPage = useBrokerStocks(code, stocksQuery)
  const peers = useBrokerPeers(code)
  const tradeSplit = useBrokerTradeSplit(code, period)
  const phrase = windowPhrase(period)

  const brokerName = detail.data?.header.name ?? brokerDisplayName(code, null)
  // `brokerShortName`'s own doc: the picker keeps the full name in `title` and
  // the accessible name — only its visible text is the stripped short form, so
  // "Naasa Securities Co. Ltd." prints as "Naasa" on a control that otherwise
  // crowds out the period picker beside it. The cards below name the house by
  // it too, as the revamp's heads do; the header card above them prints the
  // full legal name once.
  const picker = useBrokerPicker(code, brokerName)
  const brokerShortLabel = picker.brokerShortLabel
  const triggerLabel = brokerFullName(code, detail.data?.header.name ?? null)

  /**
   * `detail.data === null` means BROKER_NOT_FOUND, caught in
   * `brokerDetailQueryOptions`'s own `queryFn` rather than thrown
   * (docs/route-loading.md, "Not-found answers") — the same shape Stock's
   * `company === null` uses. A bad code has none of the five blocks below to
   * draw (they all read this one payload), only a way out — full-page, like
   * Stock, rather than a `notFound` prop threaded through five cards that
   * would each otherwise show their OWN "could not load" from the same `isError` this used to be.
   */
  if (detail.data === null) {
    return (
      <ScreenLayout header={HEADER} pageTitle="By Broker">
        <EmptyState
          kind="not-found"
          size="page"
          title="No broker with this number"
          description={`NEPSE has no broker ${code}. Check the number, or pick one from All brokers.`}
          action={
            <Link to="/brokers/all" className={stateActionClass('page')}>
              Browse all brokers
            </Link>
          }
        />
      </ScreenLayout>
    )
  }

  /**
   * One payload feeds five blocks here, and the period switcher re-keys it.
   *
   * `keepPreviousData` holds `detail.isPending` FALSE across that re-key, and
   * every one of those five blocks is handed `isLoading={detail.isPending}` and
   * nothing else — so switching from Today to 1M left the header, all three
   * ranked tables, the vs-market card and the holdings card showing Today's
   * figures under a 1M chip, unmarked, for the whole fetch.
   *
   * The fix is NOT to route `isPlaceholderData` into those `isLoading` props:
   * that would collapse five populated blocks into five skeletons on every period
   * click, which is the blanking `keepPreviousData` was added to stop. The
   * figures stay up; the screen says they are last period's.
   */
  const isUpdating = detail.isPlaceholderData

  // Issue 17 (2026-09-29)'s ruling against a routine "As of …" caption applies
  // here exactly as it does on Overview's `FloorStatsStrip`: a `today` read or
  // one that fully caught up prints nothing, and only a genuinely partial
  // window (the live fallback could only answer for a single session) gets
  // `WindowNote`'s warning banner.
  const hasPartialWindow =
    detail.data === undefined ? false : partialWindowNote(detail.data.window) !== null

  const goToBroker = (next: string) => {
    // A different house is a different table: its search term, sort and page
    // do not carry over, so the URL starts this broker at the defaults rather
    // than at page 4 of someone else's book.
    void navigate({
      to: '/brokers/$code',
      params: { code: next },
      search: defaultSearch(tab, period),
    })
  }

  const changePeriod = (next: BrokerPeriod) => {
    // Page 1 of a month is not page 1 of a day, and landing on page 4 of a
    // shorter list would read as a broken table.
    void navigate({ search: (prev) => ({ ...prev, period: next, page: 1 }) })
  }

  const changeTab = (next: BrokerTab) => {
    void navigate({ search: (prev) => ({ ...prev, tab: next }) })
  }

  const retryDetail = () => {
    void detail.refetch()
  }

  return (
    <ScreenLayout header={HEADER} pageTitle="By Broker">
      <BrokerControls
        brokers={picker.brokers}
        code={code}
        brokerLabel={picker.brokerLabel}
        triggerLabel={triggerLabel}
        period={period}
        onBrokerChange={goToBroker}
        onPeriodChange={changePeriod}
      />
      <UpdatingNote when={isUpdating} label="Updating this period…" />

      {/* Everything this wrapper holds reads `detail`, so it is all last
          period's data until the new payload lands. One refresh region,
          because it changes as one block. */}
      <RefreshRegion updating={isUpdating} className="flex flex-col gap-6">
        {hasPartialWindow ? <WindowNote window={detail.data?.window} /> : null}

        <BrokerHeaderCard
          header={detail.data?.header}
          kpis={detail.data?.kpis}
          flow={detail.data?.flow}
          vsMarket={detail.data?.vsMarket}
          code={code}
          windowPhrase={phrase}
          isLoading={detail.isPending}
          isError={detail.isError}
          // Always false: the page-level early return above already owns
          // BROKER_NOT_FOUND, so this card never needs to draw its own copy
          // of it. Kept on the prop (not removed) — a defensive shape, not a
          // reachable one.
          notFound={false}
          onRetry={retryDetail}
        />

        <BrokerPerformanceCard
          detail={detail.data}
          tradeSplit={{
            available: BROKER_TRADE_SPLIT_AVAILABLE,
            data: tradeSplit.data,
            isLoading: tradeSplit.isPending,
            isError: tradeSplit.isError,
            onRetry: () => {
              void tradeSplit.refetch()
            },
          }}
          brokerName={brokerShortLabel}
          tab={tab}
          onTabChange={changeTab}
          isLoading={detail.isPending}
          isError={detail.isError}
          onRetry={retryDetail}
        />

        <BrokerStockTables
          topBought={detail.data?.topBought ?? []}
          topSold={detail.data?.topSold ?? []}
          topNet={detail.data?.topNet ?? []}
          isLoading={detail.isPending}
          isError={detail.isError}
          onRetry={retryDetail}
        />

        <BrokerVsMarketCard
          vsMarket={detail.data?.vsMarket}
          brokerName={brokerShortLabel}
          isLoading={detail.isPending}
          isError={detail.isError}
          onRetry={retryDetail}
        />

        <BrokerHoldingsCard
          sharesHeld={detail.data?.sharesHeld ?? []}
          moreSold={detail.data?.moreSold ?? []}
          brokerName={brokerShortLabel}
          windowPhrase={phrase}
          isLoading={detail.isPending}
          isError={detail.isError}
          onRetry={retryDetail}
        />
      </RefreshRegion>

      <BrokerStockActivityCard
        brokerName={brokerShortLabel}
        windowPhrase={phrase}
        rows={stocksPage.data?.rows ?? []}
        page={stocksPage.data?.page ?? search.page}
        pageSize={search.pageSize}
        total={stocksPage.data?.total ?? 0}
        search={search.q ?? ''}
        onSearchChange={(q) => {
          // A blank field drops `q` from the URL rather than parking an empty
          // string in it — `brokerStocksQuerySchema` has `q` optional, and the
          // service omits an absent one from the request.
          const trimmed = q.trim()
          void navigate({
            search: (prev) => {
              const next = { ...prev, page: 1 }
              if (trimmed === '') delete next.q
              else next.q = trimmed
              return next
            },
          })
        }}
        onPageChange={(page) => {
          void navigate({ search: (prev) => ({ ...prev, page }) })
        }}
        onPageSizeChange={(pageSize) => {
          // Narrowed through the closed PAGE_SIZES list the pager itself draws
          // from, so a rogue value can never reach the request or the URL.
          void navigate({
            search: (prev) => ({ ...prev, pageSize: toPageSize(pageSize, prev.pageSize), page: 1 }),
          })
        }}
        isLoading={stocksPage.isPending}
        isError={stocksPage.isError}
        onRetry={() => {
          void stocksPage.refetch()
        }}
      />

      <BrokerPeersCard
        peers={peers.data ?? []}
        brokerName={brokerShortLabel}
        isLoading={peers.isPending}
        isError={peers.isError}
        onRetry={() => {
          void peers.refetch()
        }}
      />
    </ScreenLayout>
  )
}
